import { action, computed, IObservableArray, makeAutoObservable, makeObservable, observable, runInAction } from 'mobx';
import _ from 'lodash';
import { aggregateAvg, aggregateCount, aggregateCountDistinct, aggregatePercentCountOfTotal, aggregateSum } from './table.aggregators';
import { formatNum } from 'app/utils';

export enum AggregrationType {
	empty = 'empty',
	avg = 'avg',
	percentCountOfTotal = 'percentCountOfTotal',
	sum = 'sum',
	count = 'count',
	countDistinct = 'countDistinct',
}

export interface ITableAggregateColumn<T, P> {
	render?: (tm: TableModel<T, P>) => JSX.Element;
	path?: string | string[];
	format?: { (val: any): string };
	aggType?: AggregrationType;
	aggIf?: (val: any) => boolean;
	visible?: boolean;
	aggCustomSymbol?: React.ReactNode;
	aggInfo?: React.ReactNode;
	aggModifier?: string;
	rowAgg?: boolean;
}

export class TableAggregrateColumn<T, P> {
	constructor(opts: ITableAggregateColumn<T, P>, tm: TableModel<T, P>) {
		makeAutoObservable(this);
		this.tm = tm;
		this.format = opts.format;
		this.path = opts.path!; // is enforced before constructor
		this.render = opts.render;
		if (opts.aggType) {
			this.aggType = opts.aggType;
		}
		if (opts.aggIf) {
			this.aggIf = opts.aggIf;
		}
		if (opts.visible !== undefined) {
			this.visible = opts.visible;
		}
		if (opts.aggCustomSymbol) {
			this.aggCustomSymbol = opts.aggCustomSymbol;
		}
		if (opts.aggInfo) {
			this.aggInfo = opts.aggInfo;
		}
		if (opts.aggModifier) {
			this.aggModifier = opts.aggModifier;
		}
		if (opts.rowAgg) {
			this.rowAgg = opts.rowAgg;
		}
	}

	aggIf(val: any) {
		if (val > 0) {
			return true;
		}
		return false;
	}

	visible: boolean = true;

	path: string | string[];
	aggType: AggregrationType = AggregrationType.sum;
	aggCustomSymbol?: React.ReactNode;
	aggInfo?: React.ReactNode;
	aggModifier?: string;
	format?: { (val: any): string };
	tm: TableModel<T, P>;
	render?: (tm: TableModel<T, P>, aggValue?: number) => JSX.Element;
	rowAgg: boolean = false;
	doFormat(val: any) {
		if (!this.format) {
			return formatNum(val);
		}
		return this.format(val);
	}
	@computed
	get aggValue() {
		if (this.aggType === AggregrationType.sum) {
			return aggregateSum(this.tm.data, this.path! as string, this.aggIf);
		}
		if (this.aggType === AggregrationType.avg) {
			return aggregateAvg(this.tm.data, this.path! as string, this.aggIf);
		}
		if (this.aggType === AggregrationType.percentCountOfTotal) {
			return aggregatePercentCountOfTotal(this.tm.data, this.path! as string, this.aggIf);
		}
		if (this.aggType === AggregrationType.count) {
			return aggregateCount(this.tm.data, this.path! as string, this.aggIf);
		}
		if (this.aggType === AggregrationType.countDistinct) {
			return aggregateCountDistinct(this.tm.data, this.path! as string, this.aggIf);
		}
		return undefined;
	}
}

export interface ITableColumnGroup {
	label: string | JSX.Element;
	modifier?: string;
}

export interface ITableColumn<T, P> {
	label: string | string[] | JSX.Element;
	icon?: string;
	path: string | string[];
	sortBy?: string | rowSortable<T>;
	editable?: boolean;
	editType?: 'number' | 'checkbox' | 'segment';
	render?: (row: TableRow<T>) => JSX.Element;
	headerRender?: (tm: TableModel<T, P>) => JSX.Element;
	format?: { (val: any): string } | { (val: any): string }[] | { (val: any): JSX.Element } | { (val: any): JSX.Element }[];
	agg?: ITableAggregateColumn<T, P> | ITableAggregateColumn<T, P>[];
	width?: number;
	cellModifier?: string;
	labelModifier?: string;
	sortType?: SortType;
	columnGroup?: ITableColumnGroup;
}

export class TableColumn<T, P> {
	constructor(opts: ITableColumn<T, P>, tm: TableModel<T, P>) {
		makeAutoObservable(this);
		this.label = opts.label;
		this.icon = opts.icon;
		this.format = opts.format;
		this.path = opts.path;
		this.sortBy = opts.sortBy;
		this.width = opts.width;
		this.cellModifier = opts.cellModifier;
		this.labelModifier = opts.labelModifier;
		this.sortType = opts.sortType || 'string';
		this.columnGroup = opts.columnGroup;

		this.render = opts.render;
		this.headerRender = opts.headerRender;
		if (opts.agg) {
			if (Array.isArray(opts.agg)) {
				this.aggs = opts.agg.map((o) => {
					if (!o.path) {
						o.path = this.path;
					}
					return new TableAggregrateColumn(o, tm);
				});
			} else {
				this.aggs = [];
				if (!opts.agg.path) {
					opts.agg.path = this.path;
				}
				const agg = new TableAggregrateColumn(opts.agg, tm);
				this.aggs.push(agg);
			}
		}
		if (opts.editable === true) {
			this.editable = true;
			if (opts.editType) {
				this.editType = opts.editType;
			}
		}
	}
	label: string | string[] | JSX.Element;
	icon?: string;
	cellModifier?: string;
	labelModifier?: string;
	width?: number;
	path: string | string[];
	aggs?: TableAggregrateColumn<T, P>[];
	editType: 'number' | 'checkbox' | 'segment' = 'number';
	columnGroup?: ITableColumnGroup;

	@computed
	get keyPath() {
		if (Array.isArray(this.path)) {
			return this.path[0];
		}
		return this.path;
	}

	@computed
	get qsSortPath() {
		return 's_' + this.keyPath;
	}

	@computed
	get rowAgg() {
		if (!this.aggs) {
			return;
		}
		if (Array.isArray(this.aggs)) {
			const a = this.aggs.find((a) => a.rowAgg);
			if (a) {
				return a;
			}
			return this.aggs[0];
		}
		return this.aggs;
	}

	sortBy?: string | rowSortable<T>;
	sortType: SortType = 'string';
	format?: { (val: any): string } | { (val: any): string }[] | { (val: any): JSX.Element } | { (val: any): JSX.Element }[];

	doFormat(val: any, index: number) {
		if (!this.format) {
			return val;
		}
		if (Array.isArray(this.format)) {
			let fmt = this.format[index];
			if (!fmt) {
				fmt = this.format[0];
			}
			return fmt(val);
		} else {
			return this.format(val);
		}
	}

	render?: (row: TableRow<T>) => JSX.Element;
	headerRender?: (tm: TableModel<T, P>) => JSX.Element;

	@observable
	editable: boolean = false;
}

export type rowSortable<T> = (data: T) => any;
export type SortType = 'string' | 'number' | 'date';

export class TableRow<T> {
	constructor(data: T) {
		makeObservable(this);
		this.data = data;
	}
	@observable data: T;
	@observable selected: boolean = false;
	@observable hovering: boolean = false;

	@observable isGroupStart: boolean = false;
	@observable groupLabel: string = '';

	@action
	setData(path: string, val: any) {
		_.set(this.data as any, path, val);
	}
}

export class TableModel<T, P> {
	constructor() {
		makeObservable(this);
		this.rows = observable([]);
	}
	idProperty: string = 'id';
	idType: 'number' | 'string' = 'number';
	@observable columns: TableColumn<T, P>[] = [];
	@observable rows: IObservableArray<TableRow<T>>;

	renderBatchSize: number = 150;

	@observable
	currentBatchStep: number = 0;

	@action
	showMoreRows() {
		this.currentBatchStep++;
	}

	@computed
	get showingAllRows() {
		return this.visibleRowCount === this.rows.length;
	}

	@computed
	get batchedRows() {
		const batchSize = this.renderBatchSize;
		const batches: TableRow<T>[][] = [];
		if (this.rows.length < batchSize) {
			return [this.rows];
		}
		for (let i = 0; i < this.rows.length; i += batchSize) {
			batches.push(this.rows.slice(i, i + batchSize));
		}
		return batches;
	}

	@computed
	get firstBatch() {
		return this.batchedRows[0];
	}

	@computed
	get otherBatches() {
		return this.batchedRows.slice(1, this.currentBatchStep);
	}

	@computed
	get visibleRowCount() {
		let size = this.batchedRows[0].length;
		this.otherBatches.forEach((b) => (size += b.length));
		return size;
	}

	_showIndx: boolean = false;

	showHeader: boolean = true;

	groupHeaderRow?: ITableColumn<T, P>;

	@computed
	get showSum() {
		const aggCols = this.columns.filter((c) => c.aggs);
		if (aggCols.length > 0) {
			return true;
		}
		return false;
	}

	@observable selectedId?: P;
	@action
	setSelectedId(id?: P) {
		this.selectedId = id;
		this.doSelectRow();
	}

	@observable hoverId?: P;
	@action
	setHoverId(id?: P) {
		this.hoverId = id;
		this.doHoverRow();
	}

	@observable sortDisabled: boolean = false;
	@observable sortBy?: string | rowSortable<T>;
	@observable sortAsc: boolean = true;

	@computed
	get qsSortBy() {
		if (this.sortBy && typeof this.sortBy === 'string') {
			return this.sortBy;
		}
	}

	@computed
	get columnCount() {
		if (this._showIndx) {
			return this.columns.length + 1;
		}
		return this.columns.length;
	}

	@computed
	get hasColumnGroups() {
		return this.columns.some((c) => !!c.columnGroup);
	}

	@computed
	get columnGroupCells() {
		const cells: { columnGroup?: ITableColumnGroup; colSpan: number }[] = [];
		// ungrouped columns never merge, so each keeps its own header cell (e.g. the sticky first column)
		const sameGroup = (a?: ITableColumnGroup, b?: ITableColumnGroup) => {
			if (!a || !b) {
				return false;
			}
			return a === b || a.label === b.label;
		};
		this.columns.forEach((col) => {
			const prev = cells[cells.length - 1];
			if (prev && sameGroup(prev.columnGroup, col.columnGroup)) {
				prev.colSpan++;
				return;
			}
			cells.push({ columnGroup: col.columnGroup, colSpan: 1 });
		});
		return cells;
	}

	@computed
	get showFooter() {
		return this.showSum || this.hasEditbableColumns;
	}

	@observable editDisabled: boolean = false;
	@computed
	get hasEditbableColumns() {
		if (this.editDisabled) {
			return false;
		}
		const cols = this.columns.filter((x) => x.editable);
		if (cols.length > 0) {
			return true;
		}
		return false;
	}

	@observable editMode: boolean = false;
	@action
	setEditMode(val: boolean) {
		this.editMode = val;
	}
	@observable hideEditModeHeader: boolean = false;
	@action
	setHideEditModeButtons(val: boolean) {
		this.hideEditModeHeader = val;
	}
	@observable useDetachedFooter: boolean = false;
	@action
	setUseDetachedFooter(val: boolean) {
		this.useDetachedFooter = val;
	}
	@observable saveButtonText: string = 'Speichern';
	@action
	setSaveButtonText(val: string) {
		this.saveButtonText = val;
	}

	@action
	setSortBy(path: string | rowSortable<T>) {
		if (this.sortBy === path) {
			this.sortAsc = !this.sortAsc;
			return;
		}
		this.sortBy = path;
		this.sortAsc = true;
	}

	@action sort() {
		if (!this.sortBy) {
			this.group(this.rows);
			return;
		}
		const s = this.sortBy;

		// determine sortType from the active column
		const activeCol = typeof s === 'string' ? this.columns.find((c) => c.keyPath === s || c.sortBy === s) : this.columns.find((c) => c.sortBy === s);
		const sortType: SortType = activeCol?.sortType || 'string';

		const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
		let sorted = this.rows.sort((a, b) => {
			let x, y;

			if (typeof s === 'string') {
				x = _.get(a.data, s);
				y = _.get(b.data, s);
			} else {
				x = s(a.data);
				y = s(b.data);
			}

			// nulls to end
			if (x == null && y == null) return 0;
			if (sortType === 'number') {
				// direction-aware so nulls stay at end after reverse
				if (x == null) return this.sortAsc ? 1 : -1;
				if (y == null) return this.sortAsc ? -1 : 1;
			} else {
				if (x == null) return 1;
				if (y == null) return -1;
			}

			switch (sortType) {
				case 'number': {
					const nx = Number(x);
					const ny = Number(y);
					const xValid = !isNaN(nx);
					const yValid = !isNaN(ny);
					if (!xValid && !yValid) return 0;
					if (!xValid) return 1;
					if (!yValid) return -1;
					return nx - ny;
				}
				case 'date':
					const dx = x instanceof Date ? x.getTime() : new Date(x).getTime();
					const dy = y instanceof Date ? y.getTime() : new Date(y).getTime();
					return dx - dy;
				default:
					return collator.compare(x, y);
			}
		});

		if (!this.sortAsc) {
			sorted = _.reverse(sorted);
		}
		this.onSort();
		this.group(sorted);
	}

	@action group(rows: TableRow<T>[]) {
		if (!this.groupHeaderRow) {
			this.rows.replace(rows);
			return;
		}
		const groupPath = this.groupHeaderRow.path;
		rows.forEach((row, i) => {
			row.isGroupStart = true;
			const currVal = _.get(row.data, groupPath);
			row.groupLabel = currVal;
			if (i === 0) {
				return;
			}
			const previousRow = this.rows[i - 1];
			const prevVal = _.get(previousRow.data, groupPath);
			if (currVal === prevVal) {
				row.isGroupStart = false;
			}
		});
		this.rows.replace(rows);
	}

	onSort = () => { };

	@action
	setCols(cols: ITableColumn<T, P>[]) {
		this.columns = [];
		cols.forEach((c) => {
			const tc = new TableColumn<T, P>(c, this);
			this.columns.push(tc);
		});
	}

	@observable data: T[] = [];

	@action
	setGroupByData(data: T[], grouper: (i: T) => any) {
		// this.data = data;
		const groupedItems = data.reduce((groups: Record<string, T[]>, item: T) => {
			const id = grouper(item);
			// const gsId = item.agt.gsId;
			if (!groups[id]) {
				groups[id] = [];
			}
			groups[id].push(item);
			return groups;
		}, {});

		const columns = this.columns;
		const rows: TableRow<T>[] = [];
		const newData: any[] = [];
		for (const id in groupedItems) {
			const group = groupedItems[id];
			const groupy = {};
			columns.forEach((col) => {
				let val: any;
				const agg = col.rowAgg;
				if (!agg) {
					val = _.get(group[0], col.path);
					_.set(groupy, col.path, val);
					return;
				}
				const path = agg.path as string;
				switch (agg.aggType) {
					case AggregrationType.sum:
						val = aggregateSum(group, path, agg.aggIf);
						break;
					case AggregrationType.avg:
						val = aggregateAvg(group, path, agg.aggIf);
						break;
					case AggregrationType.count:
						val = aggregateCount(group, path, agg.aggIf);
						break;
					case AggregrationType.countDistinct:
						val = aggregateCountDistinct(group, path, agg.aggIf);
						break;
					case AggregrationType.percentCountOfTotal:
						val = aggregatePercentCountOfTotal(group, path, agg.aggIf);
						break;
				}
				if (isNaN(val)) {
					val = 0;
				}
				_.set(groupy, path, val);
			});
			newData.push(groupy);
			const tr = new TableRow<T>(groupy as any);
			rows.push(tr);
		}
		this.data = newData;
		this.rows.replace(rows);
		this.sort();
	}

	@action
	setPivotData(data: T[], rows: (i: T) => any, aggregateCol: ITableColumn<T, any>, horizCol: ITableColumn<T, any>) {
		const distinctHorizValues = _.uniq(data.map((x) => _.get(x, horizCol.path as string)));
		const newColumns: ITableColumn<T, any>[] = [];

		const existingLabels = this.columns.map((col) => col.label);
		distinctHorizValues.forEach((val) => {
			const newLabel = `${horizCol.label} ${val}`;
			if (!existingLabels.includes(newLabel)) {
				const newCol: ITableColumn<T, any> = {
					label: newLabel,
					path: `${horizCol.path}.${val}`,
					agg: {
						...aggregateCol.agg,
						path: `${horizCol.path}.${val}`,
					},
					format: aggregateCol.format,
					width: aggregateCol.width,
					cellModifier: aggregateCol.cellModifier,
					labelModifier: aggregateCol.labelModifier,
				};
				newColumns.push(newCol);
			}
		});
		const totalLabel = `${horizCol.label} Total`;
		if (!existingLabels.includes(totalLabel)) {
			const newCol: ITableColumn<T, any> = {
				label: totalLabel,
				path: `total`,
				agg: aggregateCol.agg,
				format: aggregateCol.format,
				width: aggregateCol.width,
				cellModifier: aggregateCol.cellModifier,
				labelModifier: aggregateCol.labelModifier,
			};
			newColumns.push(newCol);
		}
		this.setCols([...this.columns, ...newColumns]);

		const groupedData = _.groupBy(data, rows);
		const newData: any[] = [];
		const newRows: TableRow<T>[] = [];

		for (const key in groupedData) {
			const group = groupedData[key];
			const groupy = {};
			let total = 0;
			this.columns.forEach((col) => {
				const path = col.path as string;
				let val: any;
				const agg = col.rowAgg;
				if (!agg) {
					val = _.get(group[0], col.path);
					_.set(groupy, col.path, val);
					return;
				}
				// const path = agg.path as string;
				const pathParts = path.split('.');
				const lastPart = pathParts.pop();
				const basePath = pathParts.join('.');

				const subGroup = _.filter(group, (x) => _.get(x, basePath) === lastPart);
				const aggPath = aggregateCol.path as string;

				switch (agg.aggType) {
					case AggregrationType.sum:
						val = aggregateSum(subGroup, aggPath, agg.aggIf);
						break;
					case AggregrationType.avg:
						val = aggregateAvg(subGroup, aggPath, agg.aggIf);
						break;
					case AggregrationType.count:
						val = aggregateCount(subGroup, aggPath, agg.aggIf);
						break;
					case AggregrationType.countDistinct:
						val = aggregateCountDistinct(subGroup, aggPath, agg.aggIf);
						break;
					case AggregrationType.percentCountOfTotal:
						val = aggregatePercentCountOfTotal(subGroup, aggPath, agg.aggIf);
						break;
				}

				if (isNaN(val)) {
					val = 0;
				}
				if (val) {
					total += val;
				}
				_.set(groupy, path, val);
				_.set(groupy, 'total', total);
			});
			newData.push(groupy);
			const tr = new TableRow<T>(groupy as any);
			newRows.push(tr);
		}

		this.data = newData;
		this.rows.replace(newRows);
		this.sort();
	}

	@action
	setRowData(data: T[]) {
		this.data = data;
		const rows: TableRow<T>[] = [];
		this.data.forEach((d, i) => {
			const tr = new TableRow<T>(d);
			rows.push(tr);
		});
		this.rows.replace(rows);
		this.sort();
	}

	extraData: any;
	@action
	setExtraData(x: any) {
		this.extraData = x;
	}

	@computed
	get formData() {
		return observable({
			extra: this.extraData,
			data: this.data,
		});
	}

	onRowClick = (row: TableRow<T>) => { };
	onRowShiftClick = (row: TableRow<T>) => { };

	@computed
	get hasRowClickHandler() {
		return this.onRowClick.toString() !== '() => {}';
	}

	@action
	doSelectRow() {
		const id = this.selectedId;
		this.rows.forEach((d) => {
			runInAction(() => {
				if (this.compareById(d.data, id)) {
					d.selected = true;
					return;
				}
				d.selected = false;
			});
		});
	}

	@action
	doHoverRow() {
		const id = this.hoverId;
		this.rows.forEach((d) => {
			runInAction(() => {
				if (this.compareById(d.data, id)) {
					d.hovering = true;
					return;
				}
				d.hovering = false;
			});
		});
	}

	compareById(row1: T, id?: P) {
		const p = this.idProperty;
		return (row1 as any)[p] === id;
	}
}

export const tableDividerColumn = {
	label: '',
	path: '',
	cellModifier: 'divider-column',
	labelModifier: 'divider-column',
};
export const tableDividerColumnWithAgg = {
	label: '',
	path: '',
	cellModifier: 'divider-column',
	labelModifier: 'divider-column',
	agg: {
		path: '',
		aggType: AggregrationType.empty,
	},
};
